/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { CSSProperties, forwardRef, useEffect, useState } from 'react';
import { FormikProps, useFormik } from 'formik';
import { FileRejection, useDropzone } from 'react-dropzone';
import {
    Badge,
    Button,
    Checkbox,
    CheckboxOnChangeData,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    InfoLabel,
    Input,
    Label,
    makeStyles,
    Menu,
    MenuButtonProps,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    SelectTabData,
    SelectTabEvent,
    SplitButton,
    Switch,
    Tab,
    TabList,
    Text,
    Textarea,
    Toolbar,
    ToolbarDivider,
    Tooltip,
    tokens,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import {
    AddCircle12Regular,
    ArrowResetRegular,
    ArrowUpload16Regular,
    CheckboxUncheckedRegular,
    CheckmarkFilled,
    CheckmarkRegular,
    DeleteRegular,
    InfoRegular,
    StatusRegular,
    Target20Regular,
} from '@fluentui/react-icons';
import { Column, Row } from '../common';
import {
    Case,
    CaseDocument,
    CaseEvent,
    CaseMilestone,
    CaseStatus,
    MILESTONE_SORT_KEY_INCREMENT,
    Note,
    Task,
    TaskStatus,
    useInitialValues,
    UserAccount,
} from '../../data/types';
import { useAccountState } from '../../redux/reducers/accountReducer';
import { useAccess } from '../../auth/useAccess';
import { useUtilities } from '../../utilities/useUtilities';
import { useValidation } from '../../data/types/validation';
import { useCaseServicesContext } from '../pages/Cases/Cases/CaseServicesContext';
import { useCommonStyles } from '../common/Styles/commonStyles';
import { TimeTrackingService, useCaseDataService } from '../../data/services';
import { ConfirmationDialog } from './ConfirmationDialog';
import { AddEditTaskDialog } from './AddEditTaskDialog';
import { LinkButton } from '../controls/LinkButton';
import { CaseTaskTable } from '../pages/Cases/CaseTasks/CaseTasksTable';
import { AddEditCaseEventDialog } from './AddEditCaseEventDialog';
import { CaseEventsTable } from '../pages/Cases/CaseEvents/CaseEventsPage';
import { useMediaSizes } from '../common/Styles/useMediaSizes';
import { EditableLabel } from '../controls/EditableLabel';
import { appTokens } from '../common/Styles/appTokens';
import { Section } from '../common/Section';
import { DropdownMenu } from '../controls/Menus/DropdownMenu';
import { UserDropdown } from '../controls/Comboboxes/UserDropdown';
import { useToasts } from '../toasts/useToasts';
import { CaseNoteForm } from '../controls/CaseNoteForm';
import { NoteControl } from '../controls/NoteControl';
import { useDialogControls } from './useDialogControls';
import FileUploadManager from '../../data/services/FileUploadManager';
import { useUploadState } from '../../redux/reducers/uploadReducer';
import { useCustomDataTableFeatures } from '../controls/CustomDataTable/useCustomDataTableFeatures';
import { DocumentTable } from '../pages/Cases/CaseDocuments/DocumentTable';
import { DocumentDrawer } from '../pages/Cases/CaseDocuments/DocumentDrawer';
import { useCaseDocumentActions } from '../../redux/actions/caseDocumentActions';

const useStyles = makeStyles({
    dialog: {
        border: `1px solid ${tokens.colorBrandStroke1}`,
        maxWidth: '85vw',
        height: '85vh',
    },
    dialogLaptopScreen: {
        maxWidth: '90vw',
    },
    dialogUnderLaptopScreen: {
        maxWidth: '95vw',
    },
    completedText: {
        pointerEvents: 'none',
        userSelect: 'none',
    },
    title: {
        fontSize: '1.25rem',
    },
    documentsContentArea: {
        position: 'relative',
        minHeight: 0,
    },
    documentsDragArea: {
        position: 'absolute',
        inset: '.5rem',
        borderRadius: tokens.borderRadiusLarge,
        backgroundColor: tokens.colorBrandBackground2,
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        textAlign: 'center',
        opacity: '0',
    },
    documentsDragActive: {
        border: `3px solid ${tokens.colorBrandBackground4Static}`,
        opacity: '.4',
        zIndex: tokens.zIndexOverlay,
    },
    documentsBtn: {
        marginInline: '.25rem',
    },
    documentsUploadBtn: {
        marginBlock: '.25rem',
    },
    documentsDrawer: {
        width: '32rem',
    },
});

enum MilestoneDialogTabs {
    details = 'Details',
    tasks = 'Tasks',
    events = 'Events',
    discussion = 'Discussion',
    documents = 'Documents',
}

const MAX_FILES_PER_DROP = 20;
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024 * 1024; // 50 GB
const ACCEPTED_DOCUMENT_TYPES: Record<string, string[]> = {
    'application/pdf': ['.pdf'],
    'text/plain': [
        '.txt',
        '.log',
        '.md',
        '.markdown',
        '.rtf',
        '.ini',
        '.cfg',
        '.yaml',
        '.yml',
        '.xml',
        '.html',
        '.htm',
        '.css',
        '.js',
        '.ts',
        '.tsx',
        '.sql',
    ],
    'text/csv': ['.csv'],
    'application/json': ['.json'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'application/zip': ['.zip'],
    'application/x-zip-compressed': ['.zip'],
    'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.tiff'],
};

type AddEditMilestoneDialogProps = {
    children?: JSX.Element;
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    milestoneToEdit?: CaseMilestone;
    submitMilestone?: (milestone: CaseMilestone) => Promise<void>;
    onReopen?: (ms: CaseMilestone) => Promise<void>;
    onChangeStatus?: (status: CaseStatus) => void;
};
export const AddEditMilestoneDialog = forwardRef((props: AddEditMilestoneDialogProps, _ref) => {
    const { children, open, onOpenChange, milestoneToEdit, submitMilestone, onReopen, onChangeStatus } = props;
    void _ref;

    //Hooks
    const { caseForm, milestoneService, taskService, eventService, timeTrackingService, noteService, documentService } =
        useCaseServicesContext()!;
    const caseObj = caseForm.values;
    const { primaryAccount } = useAccountState();
    const { user } = (primaryAccount as UserAccount) ?? {};
    const access = useAccess(user);
    const initValues = useInitialValues();
    const utils = useUtilities();
    const caseDataService = useCaseDataService();
    const mediaSizes = useMediaSizes();
    const { validateCaseMilestone } = useValidation();
    const toasts = useToasts();

    //State
    // const [isOpen, setIsOpen] = useState<boolean>(false);
    const [reopenConfirmOpen, setReopenConfirmOpen] = useState<boolean>(false);
    const [tab, setTab] = useState<MilestoneDialogTabs>(MilestoneDialogTabs.details);
    const [tasksToUpdateOnSave, setTasksToUpdateOnSave] = useState<Task[]>([]);
    const [eventsToUpdateOnSave, setEventsToUpdateOnSave] = useState<CaseEvent[]>([]);
    const [eventsToDeleteOnSave, setEventsToDeleteOnSave] = useState<CaseEvent[]>([]);
    const [notesToAddOnSave, setNotesToAddOnSave] = useState<Note[]>([]);

    //Styles
    const styles = useStyles();
    const dialogSurfaceClasses = mergeClasses(
        styles.dialog,
        mediaSizes.isUnderDesktop && styles.dialogLaptopScreen,
        mediaSizes.isUnderLaptop && styles.dialogUnderLaptopScreen,
    );

    //Dialog Controls
    const [dialogOpen, onOpenChangeHandler, closeDialog] = useDialogControls(open, onOpenChange);

    //Form
    const getInitValues = (): CaseMilestone => {
        if (!!milestoneToEdit) {
            return {
                ...milestoneToEdit,
                dueDate: utils.date.validDate(milestoneToEdit?.dueDate),
            };
        } else {
            return {
                ...initValues.caseMilestone(caseObj.caseId),
                sortKey:
                    Math.max(...milestoneService.milestones.map((ms) => ms.sortKey || 0)) + MILESTONE_SORT_KEY_INCREMENT,
            };
        }
    };
    const milestoneForm = useFormik<CaseMilestone>({
        initialValues: getInitValues(),
        onSubmit: async (values, { resetForm }) => {
            const msValues = values as CaseMilestone;

            //ensure the caseid is set correctly
            msValues.caseId = caseObj.caseId;

            if (!msValues.blocked) {
                msValues.currentBlockers = '';
                msValues.blockerOwner = '';
            }

            try {
                const saveOperations: Promise<unknown>[] = [];

                //Save tasks
                if (tasksToUpdateOnSave.length > 0) {
                    saveOperations.push(taskService.upsertTasksAsync(tasksToUpdateOnSave));
                }

                //Save or delete events
                if (eventsToUpdateOnSave.length > 0) {
                    saveOperations.push(eventService.upsertCaseEventsAsync(eventsToUpdateOnSave));
                }
                if (eventsToDeleteOnSave.length > 0) {
                    saveOperations.push(
                        eventService.deleteCaseEventsAsync(
                            eventsToDeleteOnSave.map((e) => e.eventId as string),
                            msValues.caseId,
                        ),
                    );
                }

                //Save Notes
                if (notesToAddOnSave.length > 0) {
                    saveOperations.push(noteService.upsertCaseNotesAsync(notesToAddOnSave));
                }

                //Update Milestone
                if (submitMilestone) {
                    saveOperations.push(submitMilestone(msValues));
                }

                closeDialog();

                await Promise.all(saveOperations);

                if (!!milestoneToEdit) {
                    resetForm({ values });
                } else {
                    //re-derive initial values so the next milestone gets a new id and sort key
                    resetForm({ values: getInitValues() });
                }
            } catch (e) {
                console.log(e);
                toasts.notifyError('Error saving milestone', e instanceof Error ? e.message : 'Unknown error');
            }
        },
        onReset: () => {
            setTasksToUpdateOnSave([]);
            setEventsToUpdateOnSave([]);
            setEventsToDeleteOnSave([]);
            setNotesToAddOnSave([]);
            setTab(MilestoneDialogTabs.details);
        },
        validationSchema: validateCaseMilestone.schema,
    });
    const milestoneObj = milestoneForm.values;
    const resetForm = () => {
        milestoneForm.resetForm({ values: getInitValues() });
    };

    //Access
    const canEdit = () => {
        return (
            !!milestoneObj &&
            (access.cases.canCreateMilestones(milestoneObj.caseId) || access.cases.canEditMilestones(milestoneObj.caseId))
        );
    };
    const canAccessComplete = () => {
        return access.cases.canCompleteMilestones(milestoneObj.caseId);
    };
    const canReopen = () => {
        return milestoneObj.completed && access.cases.canReopenMilestones(milestoneObj.caseId);
    };

    //Logic
    const getStatuses = () => {
        return caseDataService.getTheseStatuses(caseObj.allowedStatuses || []) || caseDataService.caseStatuses;
    };
    const isOverdue = () => {
        const today = new Date();
        return !!milestoneObj.dueDate && today > new Date(milestoneObj.dueDate) && !milestoneObj.completed;
    };
    const sortMilestones = (a: CaseMilestone, b: CaseMilestone) => {
        return (a.sortKey || 0) - (b.sortKey || 0);
    };
    const isSaved = () => {
        return milestoneService.milestones?.some((ms) => ms.milestoneId == milestoneObj.milestoneId);
    };
    const isCurrent = () => {
        const incompleteMilestones = milestoneService.milestones?.filter((ms) => !ms.completed).sort(sortMilestones);
        return incompleteMilestones?.[0]?.milestoneId === milestoneObj.milestoneId;
    };
    const readyToComplete = () => {
        return (
            isCurrent() &&
            !milestoneObj.completed &&
            milestoneObj.deliverablesDelivered &&
            taskService.tasks.filter(filterTasksForMilestone).filter(filterOpenTasks).length == 0 &&
            eventService.currentEvents.filter(filterEventsForMilestone).length == 0
        );
    };
    const canBeCompleted = () => {
        if (primaryAccount?.account.settings?.cannotCompleteMilestoneBeforePrevious) {
            return isCurrent() && !milestoneObj.completed;
        } else {
            return true;
        }
    };
    const getCompleteBtnStyle = (): CSSProperties => {
        if (milestoneObj.completed) {
            return {
                backgroundColor: tokens.colorStatusSuccessBackground3,
                border: `1px solid ${tokens.colorStatusSuccessBackground3}`,
                color: tokens.colorNeutralStrokeOnBrand2,
            };
        } else if (readyToComplete()) {
            return {
                backgroundColor: tokens.colorStatusSuccessBackground2,
                border: `1px solid ${tokens.colorStatusSuccessBackground3}`,
            };
        } else if (isOverdue()) {
            return {
                backgroundColor: tokens.colorPaletteYellowBackground3,
                border: '1px solid #555',
            };
        } else {
            return {};
        }
    };

    //Filters
    const filterTasksForMilestone = (task: Task) => {
        return task.milestoneId == milestoneObj?.milestoneId;
    };
    const filterOpenTasks = (task: Task) => {
        return task.status == TaskStatus.open;
    };
    const filterEventsForMilestone = (event: CaseEvent) => {
        return event.milestoneId == milestoneObj?.milestoneId;
    };

    //UI Events
    
    const completeMilestone = async () => {
        if (!canAccessComplete()) return;

        milestoneForm.setFieldValue('completed', true);
        milestoneForm.setFieldValue('completedBy', primaryAccount?.user?.username);
        milestoneForm.setFieldValue('completedOn', new Date());

        milestoneForm.setFieldValue('deliverablesDelivered', true);
        await milestoneForm.submitForm();

        //complete remaining open tasks for this milestone
        const msTasks = taskService?.tasks?.filter(filterTasksForMilestone).filter(filterOpenTasks);
        try {
            await Promise.all(
                msTasks.map((task: Task) => {
                    return taskService.onComplete(task);
                }),
            );
        } catch {
            //nom nom nom
        }
    };
    const clickComplete = async (e?: React.MouseEvent) => {
        e?.stopPropagation?.();

        completeMilestone();

        closeDialog();
    };
    const onOpenReopenConfirmClick = (e: any) => {
        e?.stopPropagation?.();
        setReopenConfirmOpen(true);
    };
    const completeAndChangeStatus = async (stat: CaseStatus) => {
        if (!canAccessComplete()) return;

        completeMilestone();
        !!onChangeStatus && onChangeStatus(stat);
        closeDialog();
    };
    const onTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
        setTab(data.value as MilestoneDialogTabs);
    };
    const clickCancel = () => {
        setTasksToUpdateOnSave([]);
        setEventsToUpdateOnSave([]);
        setEventsToDeleteOnSave([]);
        setNotesToAddOnSave([]);
        resetForm();
        closeDialog();
    };
    const clickReopenMilestone = async () => {
        if (!!onReopen) {
            onReopen?.(milestoneObj);
            closeDialog();
        }
    };

    //Task Events
    const filterOutUpdatingTasks = (task: Task) => {
        return !tasksToUpdateOnSave.some((t) => t.taskId === task.taskId);
    };
    const getTasks = () => {
        const tasksForMilestone = [
            ...(taskService.tasks || []).filter(filterOutUpdatingTasks),
            ...tasksToUpdateOnSave,
        ].filter(filterTasksForMilestone);

        return tasksForMilestone;
    };
    const onSubmitTask = async (task: Task) => {
        const updatedTask = { ...task, milestoneId: milestoneObj.milestoneId, caseId: milestoneObj.caseId };
        setTasksToUpdateOnSave((prev) => {
            //filter out the task if it's already in there to avoid duplicates, then add the updated task
            const updatedTasks = prev.filter((t) => t.taskId !== task.taskId);
            updatedTasks.push(updatedTask);
            return updatedTasks;
        });
    };
    const onCompleteTask = async (task: Task) => {
        const updatedTask = { ...task, status: TaskStatus.completed };
        onSubmitTask(updatedTask);
    };
    const onCloseTask = async (task: Task) => {
        const updatedTask = { ...task, status: TaskStatus.closed };
        onSubmitTask(updatedTask);
    };
    const onReopenTask = async (task: Task) => {
        const updatedTask = { ...task, status: TaskStatus.open };
        onSubmitTask(updatedTask);
    };

    //Events
    const filterOutUpdatingEvents = (event: CaseEvent) => {
        return !eventsToUpdateOnSave.some((e) => e.eventId === event.eventId);
    };
    const filterOutDeletingEvents = (event: CaseEvent) => {
        return !eventsToDeleteOnSave.some((e) => e.eventId === event.eventId);
    };
    const sortEvents = (a: CaseEvent, b: CaseEvent) => {
        const aDate = !!a.date ? new Date(a.date).getTime() : 0;
        const bDate = !!b.date ? new Date(b.date).getTime() : 0;
        return aDate - bDate;
    };
    const getEvents = () => {
        const eventsForMilestone = [
            ...(eventService.events || []).filter(filterOutUpdatingEvents).filter(filterOutDeletingEvents),
            ...eventsToUpdateOnSave,
        ]
            .filter(filterEventsForMilestone)
            .sort(sortEvents);

        return eventsForMilestone;
    };
    const onSubmitEvent = async (event: CaseEvent) => {
        const updatedEvent = { ...event, milestoneId: milestoneObj.milestoneId, caseId: milestoneObj.caseId };
        setEventsToUpdateOnSave((prev) => {
            //filter out the event if it's already in there to avoid duplicates, then add the updated event
            const updatedEvents = prev.filter((e) => e.eventId !== event.eventId);
            updatedEvents.push(updatedEvent);
            return updatedEvents;
        });
    };
    const onDeleteEvent = async (event: CaseEvent) => {
        setEventsToUpdateOnSave((prev) => {
            //remove event if it is in the updated queue
            const updatedEvents = prev.filter((e) => e.eventId !== event.eventId);
            return updatedEvents;
        });
        setEventsToDeleteOnSave((prev) => {
            //filter out the event if it's already in there to avoid duplicates, then add the updated event
            const deletedEvents = prev.filter((e) => e.eventId !== event.eventId);
            deletedEvents.push(event);
            return deletedEvents;
        });
    };

    //Notes
    const filterNotesForMilestone = (note: Note) => {
        return note.milestoneId === milestoneObj?.milestoneId;
    };
    const getNotes = () => {
        const notesForMilestone = [...(noteService.notes || []), ...notesToAddOnSave].filter(filterNotesForMilestone);
        return notesForMilestone;
    };
    const onSubmitNote = async (note: Note) => {
        const updatedNote = { ...note, milestoneId: milestoneObj.milestoneId, caseId: milestoneObj.caseId };
        setNotesToAddOnSave((prev) => {
            return [...prev, updatedNote];
        });
    };

    //Effects
    useEffect(() => {
        if (!!milestoneToEdit) {
            resetForm();
        }
        // resetForm is intentionally derived from current form/context state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [milestoneToEdit]);

    useEffect(() => {
        //clear anything left behind by a previous close that bypassed cancel/submit
        if (dialogOpen) {
            resetForm();
        }
        // resetForm is intentionally derived from current form/context state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dialogOpen]);
    
    return (
        <>
            <Dialog open={dialogOpen} onOpenChange={onOpenChangeHandler} modalType='alert'>
                <DialogTrigger disableButtonEnhancement>{children}</DialogTrigger>
                <DialogSurface
                    className={dialogSurfaceClasses}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                    onDoubleClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    <DialogBody style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <DialogTitle>
                            <Row>
                                <Column gap='small'>
                                    <Row gap='small'>
                                        <Column width='auto' vAlign='middle'>
                                            <Target20Regular />
                                        </Column>
                                        <Column>
                                            <Text size={300}>{isSaved() ? 'Edit' : 'Add'} Milestone</Text>
                                        </Column>
                                    </Row>
                                    <Field validationMessage={milestoneForm.errors.name}>
                                        <EditableLabel
                                            labelClassName={styles.title}
                                            name='name'
                                            value={milestoneObj.name}
                                            onChange={milestoneForm?.handleChange}
                                            placeholder='Enter Milestone Name'
                                        />
                                    </Field>
                                </Column>
                                <Column width='auto' vAlign='bottom' padding='pb-1'>
                                    <Row gap='medium' width='auto'>
                                        {milestoneObj?.completed &&
                                            !!taskService?.tasks
                                                ?.filter(filterTasksForMilestone)
                                                .some((x) => x.status == TaskStatus.open) && (
                                                <Badge appearance='filled' size='large' color='danger'>
                                                    Open Tasks
                                                </Badge>
                                            )}
                                        {!!milestoneObj.blocked && (
                                            <Badge appearance='filled' size='large' color='danger'>
                                                Blocked
                                            </Badge>
                                        )}
                                        {isCurrent() && !milestoneObj.completed && (
                                            <>
                                                {isOverdue() ? (
                                                    <Badge appearance='filled' size='large' color='danger'>
                                                        In Progress - Overdue
                                                    </Badge>
                                                ) : (
                                                    <Badge appearance='tint' size='large' color='brand'>
                                                        In Progress
                                                    </Badge>
                                                )}
                                            </>
                                        )}
                                    </Row>
                                </Column>
                                <Column width='auto' vAlign='bottom'>
                                    <Menu positioning='below-end'>
                                        <MenuTrigger disableButtonEnhancement>
                                            {(triggerProps: MenuButtonProps) => (
                                                <SplitButton
                                                    menuButton={{
                                                        ...triggerProps,
                                                        appearance: milestoneObj.completed ? 'primary' : 'secondary',
                                                        style: getCompleteBtnStyle(),
                                                        disabled:
                                                            (!canAccessComplete() || !canBeCompleted()) && !canReopen(),
                                                    }}
                                                    primaryActionButton={{
                                                        onClick: clickComplete,
                                                        style: getCompleteBtnStyle(),
                                                        appearance: milestoneObj.completed ? 'primary' : 'secondary',
                                                        icon: milestoneObj.completed ? (
                                                            <CheckmarkFilled />
                                                        ) : (
                                                            <CheckboxUncheckedRegular />
                                                        ),
                                                        disabled: !canBeCompleted() || !canAccessComplete(),
                                                    }}
                                                >
                                                    {milestoneObj.completed ? 'Completed' : 'Complete Milestone'}
                                                </SplitButton>
                                            )}
                                        </MenuTrigger>

                                        <MenuPopover>
                                            <MenuList>
                                                {!milestoneObj.completed ? (
                                                    <>
                                                        <MenuItem
                                                            onClick={clickComplete}
                                                            icon={<CheckmarkRegular />}
                                                            disabled={!canAccessComplete()}
                                                            data-testid='case-milestones-complete-menu-item'
                                                        >
                                                            Complete Milestone
                                                        </MenuItem>

                                                        {!!getStatuses()?.length && !!onChangeStatus && (
                                                            <Menu>
                                                                <MenuTrigger disableButtonEnhancement>
                                                                    <MenuItem
                                                                        icon={<StatusRegular />}
                                                                        disabled={!canAccessComplete()}
                                                                        data-testid='case-milestones-complete-change-status-menu-item'
                                                                    >
                                                                        Complete + Change Status
                                                                    </MenuItem>
                                                                </MenuTrigger>
                                                                <MenuPopover>
                                                                    <MenuList>
                                                                        {getStatuses()?.map((stat: CaseStatus) => (
                                                                            <MenuItem
                                                                                key={`${stat.systemStatus}-${stat.status}`}
                                                                                onClick={() => completeAndChangeStatus(stat)}
                                                                                data-testid='case-milestones-complete-change-status-menu-item'
                                                                            >
                                                                                {stat.status}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </MenuList>
                                                                </MenuPopover>
                                                            </Menu>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        <MenuItem
                                                            icon={<ArrowResetRegular />}
                                                            onClick={onOpenReopenConfirmClick}
                                                            data-testid='case-milestones-reopen-menu-item'
                                                        >
                                                            Reopen this Milestone
                                                        </MenuItem>
                                                    </>
                                                )}
                                            </MenuList>
                                        </MenuPopover>
                                    </Menu>
                                    {readyToComplete() && (
                                        <Badge
                                            appearance='filled'
                                            color='warning'
                                            shape='circular'
                                            size='small'
                                            style={{
                                                position: 'absolute',
                                                top: '10px',
                                                left: '-10px',
                                                transform: 'rotate(-20deg)',
                                            }}
                                        >
                                            Ready
                                        </Badge>
                                    )}
                                </Column>
                            </Row>
                        </DialogTitle>
                        <DialogContent style={{ height: '100%' }}>
                            <Column flex='1' gap='small' padding='pr-2' overflowY='clip'>
                                <Column
                                    height='auto'
                                    padding='pb-2'
                                    style={{ borderBlockEnd: `1px solid ${appTokens.text.colors.subtle}` }}
                                >
                                    <TabList selectedValue={tab} onTabSelect={onTabSelect}>
                                        <Tab value={MilestoneDialogTabs.details}>Details</Tab>
                                        <Tab value={MilestoneDialogTabs.tasks}>Tasks</Tab>
                                        <Tab value={MilestoneDialogTabs.events}>Events</Tab>
                                        <Tab value={MilestoneDialogTabs.discussion}>Discussion</Tab>
                                        <Tab value={MilestoneDialogTabs.documents}>Documents</Tab>
                                    </TabList>
                                </Column>

                                {tab == MilestoneDialogTabs.details && (
                                    <DetailsTab milestoneForm={milestoneForm} caseObj={caseObj} canEdit={canEdit} />
                                )}

                                {tab == MilestoneDialogTabs.tasks && (
                                    <TasksTab
                                        milestoneObj={milestoneObj}
                                        caseObj={caseObj}
                                        canCreateTasks={access.cases.canCreateTasks()}
                                        tasks={getTasks()}
                                        timeTrackingService={timeTrackingService}
                                        onSubmitTask={onSubmitTask}
                                        onCompleteTask={onCompleteTask}
                                        onCloseTask={onCloseTask}
                                        onReopenTask={onReopenTask}
                                    />
                                )}

                                {tab == MilestoneDialogTabs.events && (
                                    <EventsTab
                                        events={getEvents()}
                                        milestoneObj={milestoneObj}
                                        canEditEvents={access.cases.canEditEvents(caseObj.caseId)}
                                        onSubmitEvent={onSubmitEvent}
                                        onDeleteEvent={onDeleteEvent}
                                    />
                                )}

                                {tab == MilestoneDialogTabs.discussion && (
                                    <NotesTab
                                        milestoneObj={milestoneObj}
                                        canAddNotes={access.cases.canAddNotes()}
                                        onSubmitNote={onSubmitNote}
                                        notes={getNotes()}
                                    />
                                )}

                                {tab == MilestoneDialogTabs.documents && (
                                    <DocumentsTab
                                        caseObj={caseObj}
                                        milestoneObj={milestoneObj}
                                        accountId={primaryAccount?.account.accountId}
                                        docs={documentService.docs || []}
                                        canEditDocs={!!access.cases.canEditDocuments(caseObj.caseId)}
                                        canDeleteDocs={!!access.cases.canDeleteDocuments(caseObj.caseId)}
                                        canUploadDocs={!!access.cases.canUploadDocuments(caseObj.caseId)}
                                        onDeleteDocs={documentService.deleteCaseDocumentsAsync}
                                        onModifyDocument={documentService.upsertCaseDocumentAsync}
                                        onReloadDocuments={documentService.loadDocumentsAsync}
                                    />
                                )}
                            </Column>
                        </DialogContent>
                        <DialogActions fluid style={{ justifyContent: 'flex-end' }}>
                                <Button
                                    type='submit'
                                    appearance='primary'
                                    onClick={() => milestoneForm.submitForm()}
                                    disabled={
                                        milestoneForm.isSubmitting ||
                                        !milestoneForm.isValid ||
                                        !canEdit() ||
                                        (!milestoneForm.dirty &&
                                            tasksToUpdateOnSave.length === 0 &&
                                            eventsToUpdateOnSave.length === 0 &&
                                            eventsToDeleteOnSave.length === 0 &&
                                            notesToAddOnSave.length === 0)
                                    }
                                    data-testid='task-dialog-save-button'
                                >
                                    Save
                                </Button>
                            <DialogTrigger disableButtonEnhancement>
                                {!!milestoneForm.dirty ||
                                tasksToUpdateOnSave.length > 0 ||
                                eventsToUpdateOnSave.length > 0 ||
                                eventsToDeleteOnSave.length > 0 ||
                                notesToAddOnSave.length > 0 ? (
                                    <ConfirmationDialog
                                        icon={<ArrowResetRegular />}
                                        title='Discard changes?'
                                        text='You have unsaved changes. Are you sure you want to discard them?'
                                        onConfirm={clickCancel}
                                    >
                                        <Button
                                            appearance='outline'
                                            // onClick={clickCancel}
                                            data-testid='task-dialog-cancel-button'
                                        >
                                            Cancel
                                        </Button>
                                    </ConfirmationDialog>
                                ) : (
                                    <Button
                                        appearance='secondary'
                                        onClick={clickCancel}
                                        data-testid='task-dialog-cancel-button'
                                    >
                                        Cancel
                                    </Button>
                                )}
                            </DialogTrigger>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
            <ConfirmationDialog
                icon={<ArrowResetRegular />}
                title={`Reopen Milestone - ${milestoneObj.name}`}
                text={
                    <Column gap='medium'>
                        <Text size={400}>Are you sure you want to reopen this milestone?</Text>
                        <Text size={300}>
                            Reopening this milestone will cause all subsequent milestones to be reopened as well.
                        </Text>
                    </Column>
                }
                open={reopenConfirmOpen}
                onOpenChange={(isOpen: boolean) => setReopenConfirmOpen(isOpen)}
                onConfirm={clickReopenMilestone}
            />
        </>
    );
});

type DetailsTabProps = {
    milestoneForm: FormikProps<CaseMilestone>;
    canEdit: () => boolean | undefined;
    caseObj: Case;
};
const DetailsTab = ({ milestoneForm, canEdit, caseObj }: DetailsTabProps) => {
    const milestoneObj = milestoneForm.values;

    //Hooks
    const { validateCaseMilestone } = useValidation();

    //Styles
    const commonStyles = useCommonStyles();
    const fieldClasses = mergeClasses(
        //styles.field,
        commonStyles.text.field,
    );

    //Logic
    const onDeliverablesDeliveredChange = (e: any, data: CheckboxOnChangeData) => {
        e?.stopPropagation?.();
        milestoneForm.setFieldValue('deliverablesDelivered', data.checked);
    };
    const onSelectDueDate = (date: Date | null | undefined) => {
        milestoneForm.setFieldValue('dueDate', new Date(date || 0));
    };
    const onSelectEscalationDate = (date: Date | null | undefined) => {
        milestoneForm.setFieldValue('escalationDate', new Date(date || 0));
    };
    const onBlockedChange = (e: any, data: CheckboxOnChangeData) => {
        e?.stopPropagation?.();
        milestoneForm.setFieldValue('blocked', !!data.checked);
    };

    return (
        <Column flex='1' height='100%' padding='py-2' className={commonStyles.scroll.classes.scrollbarClasses} scroll>
            <Row>
                <Column gap='medium' flex='4' padding='pr-2'>
                    <Section title='Details'>
                        {/* Milestone Description */}
                        <Field
                            label='Description'
                            className={fieldClasses}
                            validationMessage={milestoneForm.errors.description}
                            hint={`${milestoneObj.description?.length || 0} / ${validateCaseMilestone.description.maxLength}`}
                        >
                            <Textarea
                                name='description'
                                value={milestoneObj.description || ''}
                                onChange={milestoneForm?.handleChange}
                                tabIndex={2}
                                rows={3}
                                readOnly={!canEdit()}
                                placeholder={`Enter a description of this milestone`}
                                data-testid='case-milestones-description-textarea'
                                maxLength={validateCaseMilestone.description.maxLength}
                            />
                        </Field>

                        {/* Milestone Deliverables */}
                        <Field
                            className={fieldClasses}
                            label={
                                <Row>
                                    <Column>
                                        <InfoLabel
                                            label='Milestone Deliverables'
                                            info={
                                                <Text size={200}>
                                                    Make a note of the deliverables expected by the end of this milestone.
                                                </Text>
                                            }
                                        />
                                    </Column>
                                    <Column width='auto'>
                                        <Checkbox
                                            label='Delivered?'
                                            checked={milestoneObj.deliverablesDelivered}
                                            onChange={onDeliverablesDeliveredChange}
                                            data-testid='case-milestones-delivered-checkbox'
                                        />
                                    </Column>
                                </Row>
                            }
                            hint={`${milestoneObj.deliverables?.length || 0} / ${validateCaseMilestone.deliverables.maxLength}`}
                        >
                            <Textarea
                                name='deliverables'
                                value={milestoneObj.deliverables || ''}
                                onChange={milestoneForm?.handleChange}
                                tabIndex={3}
                                rows={4}
                                readOnly={!canEdit()}
                                placeholder={`Enter a description of the deliverables of this milestone`}
                                data-testid='case-milestones-deliverables-textarea'
                                maxLength={validateCaseMilestone.deliverables.maxLength}
                            />
                        </Field>

                        {/* Results Summary */}
                        <Field
                            label='Results Summary'
                            className={fieldClasses}
                            validationMessage={milestoneForm.errors.resultSummary}
                            hint={`${milestoneObj.resultSummary?.length || 0} / ${validateCaseMilestone.resultSummary.maxLength}`}
                        >
                            <Textarea
                                name='resultSummary'
                                value={milestoneObj.resultSummary || ''}
                                onChange={milestoneForm?.handleChange}
                                tabIndex={2}
                                rows={3}
                                readOnly={!canEdit()}
                                data-testid='case-milestones-results-summary-textarea'
                                maxLength={validateCaseMilestone.resultSummary.maxLength}
                            />
                        </Field>
                    </Section>
                </Column>

                <Column flex='2' padding='px-2'>
                    <Section title='Planning'>
                        {/* Category */}
                        <Field label='Category' className={fieldClasses} validationMessage={milestoneForm.errors.category}>
                            <Input
                                name='category'
                                value={milestoneObj.category || ''}
                                onChange={milestoneForm?.handleChange}
                                tabIndex={2}
                                readOnly={!canEdit()}
                                placeholder={`Enter a category`}
                                data-testid='case-milestones-category-input'
                            />
                        </Field>

                        {/* Priority */}
                        <Field label='Priority' className={fieldClasses} validationMessage={milestoneForm.errors.priority}>
                            <DropdownMenu
                                appearance='outline'
                                size='medium'
                                options={[1, 2, 3, 4].map((opt) => ({
                                    label: `Priority ${opt}`,
                                    value: opt,
                                }))}
                                onOptionChanged={(opt) => milestoneForm.setFieldValue('priority', opt)}
                                initialValue={milestoneObj.priority!}
                                dataTestId='case-milestones-priority-dropdown'
                            />
                        </Field>

                        {/* Dependencies */}
                        <Field
                            label='Dependencies'
                            className={fieldClasses}
                            validationMessage={milestoneForm.errors.externalDependencies}
                            hint={`${milestoneObj.externalDependencies?.length || 0} / ${validateCaseMilestone.externalDependencies.maxLength}`}
                        >
                            <Textarea
                                name='externalDependencies'
                                value={milestoneObj.externalDependencies || ''}
                                onChange={milestoneForm?.handleChange}
                                tabIndex={2}
                                rows={3}
                                readOnly={!canEdit()}
                                data-testid='case-milestones-external-dependencies-textarea'
                                maxLength={validateCaseMilestone.externalDependencies.maxLength}
                            />
                        </Field>

                        {/* Definition of Done */}
                        <Field
                            label='Definition of Done'
                            className={fieldClasses}
                            validationMessage={milestoneForm.errors.definitionOfDone}
                            hint={`${milestoneObj.definitionOfDone?.length || 0} / ${validateCaseMilestone.definitionOfDone.maxLength}`}
                        >
                            <Textarea
                                name='definitionOfDone'
                                value={milestoneObj.definitionOfDone || ''}
                                onChange={milestoneForm?.handleChange}
                                tabIndex={2}
                                rows={3}
                                readOnly={!canEdit()}
                                data-testid='case-milestones-definition-of-done-textarea'
                                maxLength={validateCaseMilestone.definitionOfDone.maxLength}
                            />
                        </Field>
                    </Section>

                    <Section title='Effort / Cost'>
                        {/* Estimated Hours / Cost */}
                        <Field
                            label='Estimated Hours / Cost'
                            className={fieldClasses}
                            validationMessage={milestoneForm.errors.estimatedHoursCost}
                        >
                            <Input
                                name='estimatedHoursCost'
                                value={milestoneObj.estimatedHoursCost || ''}
                                onChange={milestoneForm?.handleChange}
                                tabIndex={2}
                                readOnly={!canEdit()}
                                data-testid='case-milestones-estimated-hours-cost-input'
                            />
                        </Field>

                        {/* Actual Hours / Cost */}
                        <Field
                            label='Actual Hours / Cost'
                            className={fieldClasses}
                            validationMessage={milestoneForm.errors.actualHoursCost}
                        >
                            <Input
                                name='actualHoursCost'
                                value={milestoneObj.actualHoursCost || ''}
                                onChange={milestoneForm?.handleChange}
                                tabIndex={2}
                                readOnly={!canEdit()}
                                data-testid='case-milestones-actual-hours-cost-input'
                            />
                        </Field>

                        {/* Budget Variance Reason */}
                        <Field
                            label='Budget Variance Reason'
                            className={fieldClasses}
                            validationMessage={milestoneForm.errors.budgetVarianceReason}
                        >
                            <Input
                                name='budgetVarianceReason'
                                value={milestoneObj.budgetVarianceReason || ''}
                                onChange={milestoneForm?.handleChange}
                                tabIndex={2}
                                readOnly={!canEdit()}
                                data-testid='case-milestones-budget-variance-reason-input'
                            />
                        </Field>
                    </Section>
                </Column>
                <Column flex='2'>
                    <Section title='Dates / Deadlines'>
                        {/* Due Date Picker */}
                        <Column align='left' height='fit-content'>
                            <Field
                                className={fieldClasses}
                                label={
                                    <InfoLabel
                                        label='Due Date'
                                        info={
                                            <Text size={200}>
                                                Optionally set a due date for the Milestone to be completed.
                                            </Text>
                                        }
                                    />
                                }
                            >
                                <DatePicker
                                    name='dueDate'
                                    value={milestoneObj.dueDate ? new Date(milestoneObj.dueDate) : undefined}
                                    onSelectDate={onSelectDueDate}
                                    placeholder='Select a date...'
                                    readOnly={!canEdit()}
                                    minDate={caseObj.retainedDate ? new Date(caseObj.retainedDate) : undefined}
                                    data-testid='case-milestones-due-date-picker'
                                />
                            </Field>
                        </Column>

                        {/* Escalation Date Picker */}
                        <Column align='left' height='fit-content'>
                            <Field
                                className={fieldClasses}
                                label={
                                    <InfoLabel
                                        label='Escalation Date'
                                        info={
                                            <Text size={200}>Optionally set a date for the Milestone to be escalated.</Text>
                                        }
                                    />
                                }
                            >
                                <DatePicker
                                    name='escalationDate'
                                    value={milestoneObj.escalationDate ? new Date(milestoneObj.escalationDate) : undefined}
                                    onSelectDate={onSelectEscalationDate}
                                    placeholder='Select a date...'
                                    readOnly={!canEdit()}
                                    minDate={caseObj.retainedDate ? new Date(caseObj.retainedDate) : undefined}
                                    data-testid='case-milestones-escalation-date-picker'
                                />
                            </Field>
                        </Column>
                    </Section>

                    <Section title='Blockers'>
                        {/* Blocked Checkbox */}
                        <Field className={fieldClasses}>
                            <Checkbox
                                label='Is Blocked?'
                                name='blocked'
                                checked={milestoneObj.blocked}
                                onChange={onBlockedChange}
                            />
                        </Field>

                        {/* Current Blockers */}
                        {!!milestoneObj.blocked && (
                            <Field
                                label='Current Blockers'
                                className={fieldClasses}
                                validationMessage={milestoneForm.errors.currentBlockers}
                                hint={`${milestoneObj.currentBlockers?.length || 0} / ${validateCaseMilestone.currentBlockers.maxLength}`}
                            >
                                <Textarea
                                    name='currentBlockers'
                                    value={milestoneObj.currentBlockers || ''}
                                    onChange={milestoneForm?.handleChange}
                                    tabIndex={2}
                                    rows={3}
                                    readOnly={!canEdit()}
                                    data-testid='case-milestones-current-blockers-textarea'
                                    maxLength={validateCaseMilestone.currentBlockers.maxLength}
                                />
                            </Field>
                        )}

                        {/* Blocker Owner */}
                        {!!milestoneObj.blocked && (
                            <Field
                                label='Blocker Owner'
                                className={fieldClasses}
                                validationMessage={milestoneForm.errors.blockerOwner}
                            >
                                <UserDropdown
                                    appearance='outline'
                                    usersToSelectFrom={caseObj.assignedUsers}
                                    initialUserId={milestoneObj.blockerOwner}
                                    onUserSelected={(user) => milestoneForm.setFieldValue('blockerOwner', user?.userId)}
                                />
                            </Field>
                        )}
                    </Section>
                </Column>
            </Row>
        </Column>
    );
};

type TasksTabProps = {
    milestoneObj: CaseMilestone;
    tasks: Task[];
    timeTrackingService: TimeTrackingService;
    caseObj: Case;
    canCreateTasks?: boolean;
    onSubmitTask?: (task: Task) => Promise<void>;
    onCloseTask?: (task: Task) => Promise<void>;
    onCompleteTask?: (task: Task) => Promise<void>;
    onReopenTask?: (task: Task) => Promise<void>;
};
const TasksTab = ({
    milestoneObj,
    tasks,
    timeTrackingService,
    caseObj,
    canCreateTasks,
    onSubmitTask,
    onCloseTask,
    onCompleteTask,
    onReopenTask,
}: TasksTabProps) => {
    //Filter and Sort
    const sortMilestoneTasks = (a: Task, b: Task) => {
        // Primary sort: by status
        const aStat = Object.values(TaskStatus).indexOf(a.status as TaskStatus);
        const bStat = Object.values(TaskStatus).indexOf(b.status as TaskStatus);
        const statusComparison = aStat - bStat;
        if (statusComparison !== 0) {
            return statusComparison;
        }

        // Secondary sort: by due date (if available)
        if (a.lifecycle?.createdOn && b.lifecycle?.createdOn) {
            return new Date(a.lifecycle.createdOn).getTime() - new Date(b.lifecycle.createdOn).getTime();
        }

        return 0;
    };
    const filterTasksForMilestone = (task: Task) => {
        return task.milestoneId == milestoneObj?.milestoneId;
    };

    return (
        <Column flex='1' height='100%' noGap>
            <Column align='right' height='auto'>
                <Row width='auto'>
                    <Column width='auto' vAlign='middle'>
                        {milestoneObj?.completed &&
                            !!tasks?.filter(filterTasksForMilestone).some((x) => x.status == TaskStatus.open) && (
                                <Badge appearance='filled' size='large' color='danger'>
                                    Open Tasks
                                </Badge>
                            )}
                    </Column>
                    <Column width='auto'>
                        {canCreateTasks && (
                            <AddEditTaskDialog
                                onSubmit={onSubmitTask}
                                initialCaseId={caseObj?.caseId}
                                milestone={milestoneObj}
                                selectMilestone={false}
                                canChangeCase={false}
                            >
                                <LinkButton
                                    icon={<AddCircle12Regular />}
                                    text='Add Task'
                                    data-testid='case-milestones-add-task-button'
                                />
                            </AddEditTaskDialog>
                        )}
                    </Column>
                </Row>
            </Column>

            <>
                {!!tasks?.filter(filterTasksForMilestone).length ? (
                    <CaseTaskTable
                        tasks={tasks?.filter(filterTasksForMilestone).sort(sortMilestoneTasks) || []}
                        bodyHeight='calc(85vh - 18.25rem)'
                        showAssigned
                        showBillable={true}
                        showStatus
                        showMilestone={false}
                        selectMilestoneInTask={false}
                        timeTrackingService={timeTrackingService}
                        select={false}
                        onSubmit={onSubmitTask}
                        onComplete={onCompleteTask}
                        onClose={onCloseTask}
                        onReopen={onReopenTask}
                    />
                ) : (
                    <Column gap='medium' align='center' padding='py-3'>
                        <Text size={500}>You are up to date!</Text>
                        <Text size={400}>There are no Tasks remaining.</Text>
                    </Column>
                )}
            </>
        </Column>
    );
};

type EventsTabProp = {
    milestoneObj: CaseMilestone;
    canEditEvents?: boolean;
    events: CaseEvent[];
    onDeleteEvent?: (ev: CaseEvent) => Promise<void>;
    onSubmitEvent?: (ev: CaseEvent) => Promise<void>;
};
const EventsTab = ({ milestoneObj, canEditEvents, events, onDeleteEvent, onSubmitEvent }: EventsTabProp) => {
    return (
        <Column flex='1' height='100%' noGap>
            <Column align='right' height='auto'>
                <Row width='auto'>
                    <Column width='auto'>
                        {canEditEvents && (
                            <AddEditCaseEventDialog
                                onSubmit={onSubmitEvent}
                                caseId={milestoneObj?.caseId as string}
                                milestone={milestoneObj}
                            >
                                <LinkButton
                                    icon={<AddCircle12Regular />}
                                    text='Add Event'
                                    data-testid='case-milestones-add-event-button'
                                />
                            </AddEditCaseEventDialog>
                        )}
                    </Column>
                </Row>
            </Column>
            <>
                {!!events.length ? (
                    <>
                        <CaseEventsTable
                            events={events}
                            bodyHeight='calc(85vh - 18.25rem)'
                            submitEvent={onSubmitEvent}
                            deleteEvent={onDeleteEvent}
                            select={false}
                        />
                    </>
                ) : (
                    <Column gap='medium' align='center' padding='py-3'>
                        <Text size={500}>You are up to date!</Text>
                        <Text size={400}>There are no Events remaining.</Text>
                    </Column>
                )}
            </>
        </Column>
    );
};

type NotesTabProps = {
    milestoneObj: CaseMilestone;
    canAddNotes?: boolean;
    notes: Note[];
    onSubmitNote?: (note: Note) => Promise<void>;
};
const NotesTab = ({ milestoneObj, canAddNotes, notes, onSubmitNote }: NotesTabProps) => {
    const sortNotes = (a: Note, b: Note) => {
        const aDate = new Date(a.lifecycle?.createdOn || '').getTime();
        const bDate = new Date(b.lifecycle?.createdOn || '').getTime();
        return bDate - aDate;
    };
    return (
        <Column flex='1' padding='px-3'>
            {!!canAddNotes && (
                <Column width='50%'>
                    <CaseNoteForm caseId={milestoneObj?.caseId} submitNote={onSubmitNote} title='Add Comment' />
                </Column>
            )}
            <Column flex='1' gap='small'>
                <Text weight='semibold'>Discussion</Text>
                <Column flex='1' gap='medium' maxHeight='calc(85vh - 23rem)' scroll>
                    {notes?.sort(sortNotes).map((note: Note) => (
                        <NoteControl key={note.noteId} note={note} />
                    ))}
                </Column>
            </Column>
        </Column>
    );
};

type DocumentsTabProps = {
    caseObj: Case;
    milestoneObj: CaseMilestone;
    accountId?: string;
    docs: CaseDocument[];
    canEditDocs: boolean;
    canDeleteDocs: boolean;
    canUploadDocs: boolean;
    onDeleteDocs: (docIds: string[]) => Promise<void>;
    onModifyDocument: (doc: CaseDocument) => Promise<void>;
    onReloadDocuments: () => Promise<void>;
};
const DocumentsTab = (props: DocumentsTabProps) => {

    const {
        caseObj,
        milestoneObj,
        accountId,
        docs,
        canEditDocs,
        canDeleteDocs,
        canUploadDocs,
        onDeleteDocs,
        onModifyDocument,
        onReloadDocuments,
    } = props;

    //Hooks
    const toasts = useToasts();
    const uploadState = useUploadState();
    const tableFeatures = useCustomDataTableFeatures();
    const utils = useUtilities();
    const caseDocActions = useCaseDocumentActions();
    
    //Services
    const fileUploadManager: FileUploadManager = FileUploadManager.getInstance(caseDocActions);
    
    //State
    const [selectedDocs, setSelectedDocs] = useState<CaseDocument[]>([]);
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [singleSelect, setSingleSelect] = useState<boolean>(false);
    
    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const btnClasses = mergeClasses(commonStyles.button.root, styles.documentsBtn);
    const uploadBtnClasses = mergeClasses(commonStyles.button.root, styles.documentsUploadBtn);

    //Logic
    const canUpload = () => {
        return canUploadDocs && !!caseObj.caseId && !!milestoneObj.milestoneId;
    };
    const filterMilestoneDocs = (doc: CaseDocument) => {
        return utils.lifecycle.filterNotDeleted(doc) && doc.milestoneId === milestoneObj.milestoneId;
    };
    const clearSelectedDocs = () => {
        setSelectedDocs([]);
        tableFeatures.clearSelection();
    };
    const deleteSelectedDocs = async (docsToDelete: CaseDocument[]) => {
        try {
            clearSelectedDocs();

            await onDeleteDocs(docsToDelete.map((d) => d.docId as string));

            if (docsToDelete.length > 1) {
                toasts.caseDocuments.notifyCaseDocsDeleted(docsToDelete);
            } else {
                toasts.caseDocuments.notifyCaseDocDeleted(docsToDelete[0]);
            }
        } catch {
            toasts.caseDocuments.notifyCaseDocDeleteError();
        }
    };
    const describeDropRejection = (rejection: FileRejection) => {
        const reason = rejection.errors
            .map((error) => {
                if (error.code === 'file-too-large') {
                    return `exceeds ${(MAX_FILE_SIZE_BYTES / (1024 * 1024)).toFixed(0)}MB`;
                }
                if (error.code === 'file-invalid-type') {
                    return 'has an unsupported file type';
                }
                if (error.code === 'too-many-files') {
                    return `exceeds the ${MAX_FILES_PER_DROP} files per upload limit`;
                }

                return error.message;
            })
            .join(', ');

        return `${rejection.file.name}: ${reason}`;
    };

    const onSelectedDocsChanged = (selected: CaseDocument[]) => {
        setSelectedDocs(selected);
        setDrawerOpen(selected.length === 1);
    };
    const onSwitchChanged = (ev: React.ChangeEvent<HTMLInputElement>) => {
        ev.stopPropagation();
        setSingleSelect(ev.currentTarget.checked);
    };
    const onClickUpload = (e: any) => {
        e.stopPropagation();
        dropzone.open();
    };
    const onClickDetails = (e: any) => {
        e.stopPropagation();
        setDrawerOpen(true);
    };
    const onConfirmDelete = async (e: any) => {
        e.stopPropagation();
        await deleteSelectedDocs(selectedDocs);
    };
    const onUpdateDoc = async (doc: CaseDocument) => {
        try {
            await onModifyDocument({ ...doc, milestoneId: milestoneObj.milestoneId, caseId: caseObj.caseId });
            toasts.caseDocuments.notifyCaseDocUpdated(doc);
        } catch {
            toasts.caseDocuments.notifyCaseDocUpdatedError(doc);
        }
    };

    const onDrop = (acceptedFiles: File[], fileRejections: FileRejection[]) => {
        if (!milestoneObj.milestoneId) {
            toasts.notifyError('Upload unavailable', 'Please save this milestone before uploading documents.');
            return;
        }

        if (acceptedFiles.length > 0) {
            fileUploadManager.setMilestoneId(milestoneObj.milestoneId);
            fileUploadManager.queueFiles(caseObj.caseId as string, acceptedFiles);
        }

        if (fileRejections.length > 0) {
            const rejectionMessages = fileRejections.slice(0, 3).map(describeDropRejection);
            const suffix = fileRejections.length > 3 ? ` (and ${fileRejections.length - 3} more)` : '';

            toasts.notifyWarning('Some files were not added', `${rejectionMessages.join(' | ')}${suffix}`);
        }
    };

    const dropzone = useDropzone({
        onDrop,
        accept: ACCEPTED_DOCUMENT_TYPES,
        maxFiles: MAX_FILES_PER_DROP,
        maxSize: MAX_FILE_SIZE_BYTES,
        noClick: true,
        noKeyboard: true,
    });

    useEffect(() => {
        if (accountId) {
            fileUploadManager.setAccount(accountId);
        }
    }, [accountId]);
   
    useEffect(() => {
        setSelectedDocs([]);
        tableFeatures.clearSelection();
        setDrawerOpen(false);
        // Only reset selection when switching milestones; tableFeatures reference is not stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [milestoneObj.milestoneId]);

    return (
        <Column flex='1' noGap>
            <Row padding='pl-3'>
                <Column>
                    <Row noGap>
                        <Column width='auto' vAlign='middle' height='auto'>
                            <Label>Selection mode</Label>
                        </Column>
                        <Column width='auto' vAlign='middle' height='auto'>
                            <Switch
                                label='Single-select'
                                checked={singleSelect}
                                onChange={onSwitchChanged}
                                data-testid='milestone-documents-single-switch'
                            />
                        </Column>
                    </Row>
                </Column>

                <Toolbar>
                    {(canEditDocs || canDeleteDocs) && !!selectedDocs.length && (
                        <>
                            <ToolbarDivider />

                            <Text>{selectedDocs.length} Documents selected</Text>

                            <ToolbarDivider />

                            {!!canEditDocs && selectedDocs.length === 1 && (
                                <Button
                                    className={btnClasses}
                                    aria-label='Details'
                                    appearance='subtle'
                                    shape='circular'
                                    icon={<InfoRegular />}
                                    onClick={onClickDetails}
                                    data-testid='milestone-documents-details-btn'
                                />
                            )}
                            {!!canDeleteDocs && (
                                <ConfirmationDialog
                                    title='Confirm Delete'
                                    icon={<DeleteRegular />}
                                    text='Are you sure you want to delete the selected documents?'
                                    onConfirm={onConfirmDelete}
                                >
                                    <Button
                                        className={btnClasses}
                                        aria-label='Delete'
                                        icon={<DeleteRegular />}
                                        appearance='subtle'
                                        shape='circular'
                                        data-testid='milestone-documents-delete-btn'
                                    />
                                </ConfirmationDialog>
                            )}
                        </>
                    )}
                </Toolbar>

                {canUpload() && (
                    <Column width='auto' padding='pr-3' vAlign='middle'>
                        <Tooltip content='Upload documents to this milestone' relationship='label'>
                            <Button
                                className={uploadBtnClasses}
                                onClick={onClickUpload}
                                icon={<ArrowUpload16Regular />}
                                data-testid='milestone-documents-upload-btn'
                            >
                                Upload
                            </Button>
                        </Tooltip>
                    </Column>
                )}
            </Row>

            <Column noGap flex='1' overflowX='hidden' overflowY='hidden' className={styles.documentsContentArea}>
                {canUpload() && (
                    <Column
                        flex='1'
                        align='center'
                        vAlign='middle'
                        {...dropzone.getRootProps()}
                        className={mergeClasses(styles.documentsDragArea, dropzone.isDragActive && styles.documentsDragActive)}
                        data-testid='milestone-documents-drop-zone'
                    >
                        <Text size={900}>Drop Files Here...</Text>
                        <Text size={200}>Supported files only, up to 100MB each and 20 files per upload.</Text>
                        <input {...dropzone.getInputProps()} />
                    </Column>
                )}

                <Row noWrap noGap flex='1'>
                    <DocumentTable
                        docs={docs.filter(filterMilestoneDocs)}
                        singleSelect={singleSelect}
                        onSelectionChanged={onSelectedDocsChanged}
                        bodyHeight='calc(85vh - 21rem)'
                        onModifyDocument={onUpdateDoc}
                        onDeleteDocs={deleteSelectedDocs}
                        setDrawerOpen={setDrawerOpen}
                        {...tableFeatures.tableProps}
                    />
                    <DocumentDrawer
                        className={styles.documentsDrawer}
                        open={drawerOpen}
                        setOpen={setDrawerOpen}
                        position='end'
                        selectedDoc={docs.find((d) => d.docId == selectedDocs[0]?.docId)}
                        onSubmit={onUpdateDoc}
                    />
                </Row>
            </Column>
        </Column>
    );
};
